import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

import { Spinner } from "@/components/ui/spinner"; // custom: pending state
import { cn } from "@/lib/utils";

// custom: the shared control surface (docs/design-language.md), one neutral fill ladder
// on foreground alpha, so light and dark need no separate classes. `data-active` marks a
// filter carrying a value; a Button used as a toggle signals the same with `aria-pressed`.
const CONTROL_FILL =
  "border-input bg-foreground/5 hover:bg-foreground/10 aria-expanded:bg-foreground/10 data-popup-open:bg-foreground/10 data-active:border-foreground/50 data-active:bg-foreground/16 data-active:hover:bg-foreground/24 aria-pressed:border-foreground/50 aria-pressed:bg-foreground/16 aria-pressed:hover:bg-foreground/24";

const buttonVariants = cva(
  // custom: ring-2 focus/invalid rings — app-wide focus width (scaffold ships ring-3)
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // custom: corner-cut signature on the filled primary (docs/design-language.md); ring-inset because clip-path clips outset box-shadows
        default:
          "bg-primary text-primary-foreground hover:bg-primary/80 btn-corner-cut rounded-none focus-visible:ring-inset",
        // custom: outline runs the control ladder too, so a bordered button matches the
        // inputs and segmented toggles it shares a row with
        outline: `${CONTROL_FILL} hover:text-foreground aria-expanded:text-foreground`,
        // custom: corner-cut on the filled secondary too — all solid fills share the signature shape (docs/design-language.md)
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground btn-corner-cut rounded-none focus-visible:ring-inset",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        control: CONTROL_FILL,
        // custom: solid destructive fill with the corner-cut signature — destructive commits share the family shape (docs/design-language.md); text-destructive-foreground instead of text-white to track the token
        // custom: dark:hover:bg-destructive/70 — scaffold's dark:bg-destructive/60 wins over hover:bg-destructive/90 in the cascade, leaving dark mode with no hover feedback
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:hover:bg-destructive/70 dark:focus-visible:ring-destructive/40 btn-corner-cut rounded-none focus-visible:ring-inset",
        link: "text-primary underline-offset-4 hover:underline",
        // custom: muted inline text-link for secondary actions (text-muted-foreground + persistent underline); distinct from `link` which is text-primary, offset-4, hover-only underline
        "link-muted":
          "font-normal text-muted-foreground underline underline-offset-2 hover:text-foreground",
        // custom: dashed-border CTA for empty-state "add" targets (deck zones); `outline` is solid-border so it doesn't fit
        dashed:
          "border-border border-dashed text-muted-foreground hover:border-muted-foreground/50 hover:bg-muted/50 hover:text-foreground",
        // custom: translucent glass pill for a floating selector over content (card-table group jump); no other variant captures the blur + ring
        "glass-pill": "bg-background/60 ring-border/70 rounded-full shadow-sm ring-1 backdrop-blur",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        // custom: smaller corner-cut and the rounded-md token radius for the compact size (see btn-corner-cut)
        xs: "[--btn-cut:5px] h-6 gap-1 rounded-md px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        // custom: smaller corner-cut and the rounded-md token radius for the compact size (see btn-corner-cut)
        sm: "[--btn-cut:5px] h-7 gap-1 rounded-md px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        // custom: smaller corner-cut and the rounded-md token radius for the compact size (see btn-corner-cut)
        "icon-xs":
          "[--btn-cut:5px] size-6 rounded-md in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        // custom: smaller corner-cut and the rounded-md token radius for the compact size (see btn-corner-cut)
        "icon-sm": "[--btn-cut:5px] size-7 rounded-md in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

// custom: the spinner matches the icon size each button size gives its svgs
const SPINNER_SIZE: Partial<
  Record<NonNullable<VariantProps<typeof buttonVariants>["size"]>, string>
> = { xs: "size-3", "icon-xs": "size-3", sm: "size-3.5", "icon-sm": "size-3.5" };

function Button({
  className,
  variant = "default",
  size = "default",
  render,
  nativeButton,
  pending = false, // custom: pending state
  disabled, // custom: pending state
  children, // custom: pending state
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    pending?: boolean; // custom: pending state
  }) {
  // custom: default nativeButton to false when a `render` prop is supplied.
  // Render is almost always used to swap in a TanStack <Link> (an <a>), and
  // Base UI warns at runtime if nativeButton stays true on a non-<button>.
  const resolvedNativeButton = nativeButton ?? render === undefined;
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        pending && "[&>[data-slot=spinner]+svg]:hidden", // custom: the spinner stands in for the leading icon
      )}
      // custom: BaseUI's useRenderElement conditionally skips useMergedRefs on
      // the server (typeof document check), which causes the native <button>'s
      // `disabled` attribute to be absent from SSR HTML while present on the
      // client, triggering a React 19 hydration mismatch warning.
      suppressHydrationWarning
      render={render}
      nativeButton={resolvedNativeButton}
      disabled={disabled || pending} // custom: pending state
      aria-busy={pending || undefined} // custom: pending state
      {...props}
    >
      {/* custom: pending state */}
      {pending ? (
        <Spinner aria-hidden="true" className={size ? SPINNER_SIZE[size] : undefined} />
      ) : null}
      {children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
